import { useEffect } from 'react';

/**
 * Close a popover/dropdown when the user clicks outside `ref` or presses Escape.
 *
 * Encapsulates the mousedown + keydown pattern that was hand-rolled (and
 * duplicated) across many components. Guarded on `isOpen` so no global
 * listeners are attached while the element is closed.
 *
 * @param {React.RefObject} ref       Wrapper element that should stay "inside".
 * @param {boolean}         isOpen     Whether the popover is currently open.
 * @param {Function}        setIsOpen  State setter; called with `false` to close.
 *
 * Pass the React state setter directly (it is stable across renders) so the
 * effect's dependency array stays stable and only re-runs when `isOpen` flips.
 */
export function useClickOutside(ref, isOpen, setIsOpen) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [ref, isOpen, setIsOpen]);
}
